import React, { useMemo, useState } from 'react';
import { Button, Checkbox, Input } from 'antd';
import axios from 'axios';
import { API_DOMAIN } from '../constants';
import useSnackbar from '../Snackbar/UseSnackbar';
import siteConfig from '../../siteConfig';
import { countSrmChangelogItems } from '../../utils/srmVersion';

// Update this together with siteConfig.version/build_no before each rollout.
// It is only the text area's initial value, so the publisher can freely edit
// the release notes in Global Settings before broadcasting them.
const DEFAULT_CHANGELOG = `🚀 What's new in SRM v4.10.0

🧭 Operations V2 Foundation
A new dispatcher worklist keeps all active Operations orders visible, separates terminal history, highlights attention and next actions, and opens complete order information in a focused detail drawer while legacy Operations remains available.

🤝 Structured Sales Handover
Sales can now hand eligible work into Operations V2 through a dedicated handover view, with clear readiness and validation feedback.

📦 End-to-End Operations Actions
New procurement, physical custody, pickup, delivery, OTP, evidence, and logistics controls establish the core Operations V2 execution workflow.

↩️ Exceptional Paths & Payments
Legacy Operations now supports PO-group payment recording and a clearer exceptional-path decision flow for returns, refunds, and customer outcomes.

👥 Stronger Session Administration
HRM administrators can log a user out everywhere, while revoked sessions are handled consistently across live sockets, reconnects, and ordinary API requests.

⚖️ Operations Assignment Controls
Global Settings now includes Operations round-robin assignability controls for managing who participates in automatic workload distribution.

🛡️ Reliable Release Tracking
Every production release now uses a committed and tagged version boundary before building, preventing version bumps from being lost and ensuring changelogs cover all work since the previous release.

💳 Unified Payments & Operations Handover
The Sales task drawer now combines payment management and Operations handover in one balanced workspace, with live eligibility guidance, relationship indicators, delivery-address preparation, and a clearer transition into fulfilment.

🔄 Continuous Sales Visibility
Queries remain visible in the paid Sales stage after Operations begins work, with the latest Operations status and progress surfaced directly on the Sales card instead of disappearing from the pipeline.

✅ Safer Operations Approvals
Operations permission requests are now clearly identified in the approval center and open a dedicated review experience with customer, authorization, financial, fulfilment-line, and delivery-address context before a decision is made.

🧾 More Reliable Payment Workflows
Payment actions and background refreshes no longer empty the parent task drawer, nested price updates remain stable, payment dialogs layer correctly, and payment-link customer names and email addresses are populated from the full customer profile.

🧭 Cleaner Operations Navigation
Supplier Chats and Operations V2 now have a clearer navigation order and page naming, legacy Operations is retired from navigation while its source remains available for reference, and the Operations dispatcher toolbar is denser and easier to scan.`;

export function UpdateNotificationPanel() {
  const head = useMemo(() => ({ headers: { accessToken: localStorage.getItem("accessToken") } }), []);
  const { SuccessSnackbar, ErrorSnackbar } = useSnackbar();

  const [changelog, setChangelog] = useState(DEFAULT_CHANGELOG);
  const [requireLogout, setRequireLogout] = useState(false);
  const [pushing, setPushing] = useState(false);
  const changeCount = countSrmChangelogItems(changelog);

  const handlePush = async () => {
    setPushing(true);
    try {
      const res = await axios.post(`${API_DOMAIN}/whatsapp/push_new_version_event`, {
        changelog,
        version: siteConfig.version,
        build_no: siteConfig.build_no,
        published_at: new Date().toISOString(),
        require_logout: requireLogout,
        change_count: changeCount,
      }, head);
      if (res.data.success) {
        SuccessSnackbar("Update notification pushed!");
        setChangelog(DEFAULT_CHANGELOG);
        setRequireLogout(false);
      } else {
        ErrorSnackbar(res.data.message);
      }
    } catch (error) {
      ErrorSnackbar(error.message);
    } finally {
      setPushing(false);
    }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <div>
        <div style={{ fontSize: 12, color: "#595959", marginBottom: 10 }}>
          Publishing <strong>SRM v{siteConfig.version}</strong> · build <strong>{siteConfig.build_no}</strong>
        </div>
        <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 4 }}>Changelog</div>
        <div style={{ fontSize: 12, color: "#8c8c8c", marginBottom: 8 }}>
          Description of the latest software update — shown to users in the update notification.
        </div>
        <Input.TextArea
          rows={8}
          value={changelog}
          onChange={e => setChangelog(e.target.value)}
          placeholder="What's new in this update…"
        />
        <div style={{ marginTop: 6, fontSize: 12, color: "#8c8c8c" }}>
          {changeCount > 0 ? `${changeCount} update ${changeCount === 1 ? "item" : "items"} detected for the banner.` : "The banner will use a generic update message."}
        </div>
      </div>
      <Checkbox checked={requireLogout} onChange={event => setRequireLogout(event.target.checked)}>
        Require users to log in again after loading this update
      </Checkbox>
      {requireLogout && (
        <div style={{ marginTop: -6, fontSize: 12, color: "#8c8c8c" }}>
          Users will be warned that loading this version clears their current session and redirects them to the login screen.
        </div>
      )}
      <div style={{ display: "flex", justifyContent: "flex-end" }}>
        <Button type="primary" loading={pushing} disabled={!changelog.trim()} onClick={handlePush}>
          Push update notif
        </Button>
      </div>
    </div>
  );
}
